/*
A lightweight hook around the browser Web Speech API (SpeechRecognition).
Provides vendor-prefixed support and a simple start/stop interface with callbacks.
*/

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// Extend the Window interface to include webkitSpeechRecognition
declare global {
  interface Window {
    webkitSpeechRecognition?: any
  }
}

export interface UseSpeechRecognitionOptions {
  lang?: string
  interimResults?: boolean
  continuous?: boolean
  // Callback on every result change with full interim transcript and final flag
  onResult?: (data: { transcript: string; isFinal: boolean }) => void
}

export interface UseSpeechRecognitionReturn {
  supported: boolean
  listening: boolean
  error: string | null
  start: () => void
  stop: () => void
}

export function useSpeechRecognition(options: UseSpeechRecognitionOptions = {}): UseSpeechRecognitionReturn {
  const { lang = (typeof navigator !== 'undefined' ? navigator.language : 'en-US'), interimResults = true, continuous = false, onResult } = options

  const recognitionRef = useRef<any>(null)
  const [supported, setSupported] = useState<boolean>(false)
  const [listening, setListening] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  // Lazy-init recognition instance
  const getRecognition = useCallback(() => {
    if (recognitionRef.current) return recognitionRef.current

    const RecognitionCtor: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!RecognitionCtor) {
      setSupported(false)
      return null
    }

    try {
      const rec: any = new RecognitionCtor()
      rec.lang = lang
      rec.interimResults = interimResults
      rec.continuous = continuous

      rec.onresult = (event: any) => {
        let combined = ''
        for (let i = 0; i < event.results.length; i++) {
          combined += event.results[i][0].transcript
        }
        const last = event.results[event.results.length - 1]
        const isFinal = last ? last.isFinal : false
        onResult?.({ transcript: combined, isFinal })
      }

      rec.onerror = (ev: any) => {
        const msg = ev?.error || 'speech_recognition_error'
        setError(msg)
        setListening(false)
      }

      rec.onend = () => {
        setListening(false)
      }

      recognitionRef.current = rec
      setSupported(true)
      return rec
    } catch (e: any) {
      setSupported(false)
      setError(e?.message || 'speech_recognition_init_failed')
      return null
    }
  }, [lang, interimResults, continuous, onResult])

  const start = useCallback(() => {
    setError(null)
    const rec = getRecognition()
    if (!rec) return
    try {
      rec.lang = lang
      rec.interimResults = interimResults
      rec.continuous = continuous
      rec.start()
      setListening(true)
    } catch (e: any) {
      setError(e?.message || 'speech_recognition_start_failed')
      setListening(false)
    }
  }, [getRecognition, lang, interimResults, continuous])

  const stop = useCallback(() => {
    const rec = recognitionRef.current
    if (!rec) return
    try {
      rec.stop()
    } catch (e) {
      // ignore
    }
    setListening(false)
  }, [])

  // On mount, check support
  useEffect(() => {
    const RecognitionCtor: any = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    setSupported(!!RecognitionCtor)
  }, [])

  // Cleanup
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop()
      } catch (e) {}
      recognitionRef.current = null
    }
  }, [])

  return useMemo(() => ({ supported, listening, error, start, stop }), [supported, listening, error, start, stop])
}
